import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getEstate, updateEstate, createEstate } from '../../api/estates';
import {
  Container,
  Typography,
  Box,
  Button,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import MobileFormFooter from '../../components/common/MobileFormFooter';

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  billing_mode: Yup.string().oneOf(['estate_pays', 'members_pay']).required('Billing mode is required'),
  contact_name: Yup.string().nullable(),
  contact_phone: Yup.string().nullable(),
  contact_email: Yup.string().email('Invalid email').nullable(),
  address: Yup.string().nullable(),
  notes: Yup.string().nullable(),
});

const EstateForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const formik = useFormik({
    initialValues: {
      name: '',
      billing_mode: 'members_pay',
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      address: '',
      notes: '',
    },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  // Fetch estate data for edit mode
  useEffect(() => {
    if (isEditMode) {
      setIsLoading(true);
      getEstate(id)
        .then((estate) => {
          formik.setValues({
            name: estate.name || '',
            billing_mode: estate.billing_mode || 'members_pay',
            contact_name: estate.contact_name || '',
            contact_phone: estate.contact_phone || '',
            contact_email: estate.contact_email || '',
            address: estate.address || '',
            notes: estate.notes || '',
          });
        })
        .catch((err) => {
          console.error('Error fetching estate:', err);
          setError('Failed to load estate data.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id]);

  async function handleSubmit(values) {
    setIsSubmitting(true);
    setError(null);

    try {
      const estateData = {
        name: values.name,
        billing_mode: values.billing_mode,
        contact_name: values.contact_name || null,
        contact_phone: values.contact_phone || null,
        contact_email: values.contact_email || null,
        address: values.address || null,
        notes: values.notes || null,
      };

      if (isEditMode) {
        await updateEstate(id, estateData);
      } else {
        await createEstate(estateData);
      }

      navigate('/members?tab=estates');
    } catch (error) {
      console.error('Error saving estate:', error);
      if (error.response && error.response.data && error.response.data.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Failed to save estate. Please try again later.');
      }
      setIsSubmitting(false);
    }
  }

  const handleCancel = () => {
    navigate('/members?tab=estates');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4, pb: { xs: 10, md: 0 } }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/dashboard" color="inherit">
            Dashboard
          </Link>
          <Link component={RouterLink} to="/members?tab=estates" color="inherit">
            Estates
          </Link>
          <Typography color="text.primary">{isEditMode ? 'Edit' : 'Create'}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {isEditMode ? 'Edit Estate' : 'Create Estate'}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            component={RouterLink}
            to="/members?tab=estates"
          >
            Back to Estates
          </Button>
        </Box>

        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Estate Information
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label="Estate Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="billing-mode-label">Billing Mode</InputLabel>
                  <Select
                    labelId="billing-mode-label"
                    id="billing_mode"
                    name="billing_mode"
                    value={formik.values.billing_mode}
                    label="Billing Mode"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.billing_mode && Boolean(formik.errors.billing_mode)}
                  >
                    <MenuItem value="estate_pays">Estate Pays</MenuItem>
                    <MenuItem value="members_pay">Members Pay</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="contact_name"
                  name="contact_name"
                  label="Contact Name"
                  value={formik.values.contact_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.contact_name && Boolean(formik.errors.contact_name)}
                  helperText={formik.touched.contact_name && formik.errors.contact_name}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="contact_phone"
                  name="contact_phone"
                  label="Contact Phone"
                  value={formik.values.contact_phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.contact_phone && Boolean(formik.errors.contact_phone)}
                  helperText={formik.touched.contact_phone && formik.errors.contact_phone}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="contact_email"
                  name="contact_email"
                  label="Contact Email"
                  value={formik.values.contact_email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.contact_email && Boolean(formik.errors.contact_email)}
                  helperText={formik.touched.contact_email && formik.errors.contact_email}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="address"
                  name="address"
                  label="Address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="notes"
                  name="notes"
                  label="Notes"
                  value={formik.values.notes}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.notes && Boolean(formik.errors.notes)}
                  helperText={formik.touched.notes && formik.errors.notes}
                  multiline
                  rows={3}
                />
              </Grid>

              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancel}
                    startIcon={<CancelIcon />}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={isSubmitting ? <CircularProgress size={24} /> : <SaveIcon />}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <MobileFormFooter
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
              saveLabel={isEditMode ? 'Save Changes' : 'Create Estate'}
            />
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default EstateForm;
